import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";

const RATINGS = ["None", "Mild", "Moderate", "Severe"];

export default function QaReview() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [caseDetail, setCaseDetail] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [notes, setNotes] = useState("");
  const [acting, setActing] = useState(false);

  useEffect(() => {
    api.get(`/cases/${caseId}/report`).then((r) => {
      setReport(r.data);
      setChecklist(r.data.qa?.checklist || {});
      setNotes(r.data.qa?.notes || "");
    }).catch((e) => toast.error(errMsg(e)));
    api.get(`/clinician/cases/${caseId}`).then((r) => setCaseDetail(r.data)).catch(() => {});
  }, [caseId]);

  if (!report) return <DashboardShell title="QA review"><div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" /></DashboardShell>;

  const reviewable = report.status === "qa_review";
  const labels = report.section_labels || {};
  const checklistLabels = report.qa_checklist_labels || {};
  const allChecked = Object.keys(checklistLabels).every((k) => checklist[k]);

  const decide = async (decision) => {
    setActing(true);
    try {
      await api.post(`/qa/${caseId}/review`, { decision, checklist, notes });
      toast.success(decision === "approve" ? "Report approved and delivered." : "Report returned to clinician.");
      navigate("/admin/qa");
    } catch (e) { toast.error(errMsg(e)); } finally { setActing(false); }
  };

  return (
    <DashboardShell title={`QA review — ${report.client?.name || ""}`} subtitle={`${report.product?.name || ""} · Submitted ${report.attestation?.signed_at ? new Date(report.attestation.signed_at).toLocaleString() : ""}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8 max-w-3xl" data-testid="qa-report-content">
          {caseDetail?.responses?.length > 0 && (
            <section className="card-warm p-6">
              <h2 className="font-heading text-xl font-semibold mb-4">Scored measures</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {caseDetail.responses.map((r) => (
                  <div key={r.measure} className="border border-[#E5E5DF] rounded-lg p-3" data-testid={`qa-score-${r.measure}`}>
                    <p className="text-[11px] tracking-[0.15em] uppercase font-bold text-[#5C5C57]">{r.measure_name}</p>
                    <p className="font-heading text-2xl font-semibold">{r.score}{r.max_score ? <span className="text-sm text-[#5C5C57]">/{r.max_score}</span> : null}</p>
                    <p className="text-xs text-[#5C5C57] mt-0.5">{r.severity}</p>
                  </div>
                ))}
              </div>
              {caseDetail.flags?.length > 0 && (
                <ul className="mt-4 space-y-1 text-sm text-[#8C2A2A]">
                  {caseDetail.flags.map((f, i) => <li key={i}>• {f}</li>)}
                </ul>
              )}
            </section>
          )}
          {Object.entries(labels).map(([k, label]) => (
            <section key={k}>
              <h2 className="font-heading text-xl font-semibold mb-2">{label}</h2>
              <p className="text-sm leading-7 whitespace-pre-wrap">{report.sections?.[k] || <span className="text-[#8C2A2A]">— empty —</span>}</p>
            </section>
          ))}
          <section>
            <h2 className="font-heading text-xl font-semibold mb-2">IX. Functional Impact Matrix</h2>
            <div className="card-warm overflow-hidden">
              {(report.functional_matrix || []).map((m) => (
                <div key={m.domain} className="grid grid-cols-3 gap-4 px-5 py-2.5 border-b border-[#E5E5DF] last:border-0 text-sm">
                  <span className="font-semibold">{m.domain}</span>
                  <span>{m.rating === null || m.rating === undefined ? "—" : RATINGS[m.rating]}</span>
                  <span className="text-[#5C5C57] col-span-3 sm:col-span-1">{m.evidence}</span>
                </div>
              ))}
            </div>
          </section>
          <section className="card-warm p-5">
            <p className="text-sm"><span className="font-semibold">Signed:</span> /s/ {report.attestation?.name}{report.attestation?.credentials ? `, ${report.attestation.credentials}` : ""}</p>
          </section>
        </div>

        <div className="card-warm p-6 h-fit lg:sticky lg:top-24" data-testid="qa-checklist-panel">
          <h2 className="font-heading text-xl font-semibold mb-1">QA checklist</h2>
          <p className="text-xs text-[#5C5C57] mb-5">All items must be verified before approval.</p>
          <div className="space-y-3">
            {Object.entries(checklistLabels).map(([k, label]) => (
              <label key={k} className="flex items-start gap-3 text-sm cursor-pointer">
                <Checkbox data-testid={`qa-check-${k}`} checked={!!checklist[k]} disabled={!reviewable}
                  onCheckedChange={(v) => setChecklist((c) => ({ ...c, [k]: !!v }))} className="mt-0.5" />
                {label}
              </label>
            ))}
          </div>
          <Textarea rows={4} data-testid="qa-notes" placeholder="QA notes (required when returning)" value={notes}
            disabled={!reviewable} onChange={(e) => setNotes(e.target.value)} className="input-warm border-[#E5E5DF] mt-5 text-sm" />
          {reviewable ? (
            <div className="flex flex-col gap-2 mt-5">
              <Button data-testid="qa-approve-btn" disabled={acting || !allChecked} onClick={() => decide("approve")}
                className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
                <CheckCircle2 className="h-4 w-4 mr-2" /> Approve & deliver
              </Button>
              <Button variant="outline" data-testid="qa-return-btn" disabled={acting} onClick={() => decide("return")}
                className="rounded-full border-[#8C2A2A]/40 text-[#8C2A2A] hover:bg-red-50">
                <RotateCcw className="h-4 w-4 mr-2" /> Return for revision
              </Button>
            </div>
          ) : (
            <p className="text-sm text-[#5C5C57] mt-5" data-testid="qa-final-status">This report has been {report.status}.</p>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
